/**
 * 오더목록 DataTable 공용 모듈 — $j.F.Order.ListTable
 *
 * 관리자 접수(/adm/orders)와 협력사 접수(/p/orders)가 공유한다.
 * 두 화면은 같은 조상(레거시 skw_2000n.php)에서 갈라져 나왔고 컬럼 렌더가 동일하다.
 * 호스트는 "표시할 컬럼 key 목록" 과 "권한 옵션" 만 넘긴다.
 *
 * 사용:
 *   const $table = $j.F.Order.ListTable.create({
 *       selector : '#order-table',
 *       url      : '/p/ajax/orders/list',
 *       detailUrl: '/p/ajax/order/detail',
 *       columns  : ['orderNum','carType','balName', ...],   // 생략 시 DEFAULT_ORDER
 *       getSearch: () => $j.p.searchParams(),
 *       onLoaded : (json) => {...},                          // 응답 후처리 (합계/hidden input)
 *       canHold  : false,      // 수주자 컬럼의 홀드 input (관리자 level>=5)
 *       canPortal: false,      // 코드 컬럼의 회원포털 링크 (관리자 전용)
 *       canHandle: false,      // 오더번호 클릭 → 오더핸들 팝업 (관리자 전용)
 *       canMatch : false,      // 업종 클릭 → 매칭내역 팝업 (관리자 전용)
 *   });
 *
 * 컬럼 def 의 className/createdCell 은 각 정의 안에 인라인되어 있다.
 * (구 columnDefs targets:[0,1,2...] 인덱스 하드코딩 방식이면 컬럼 subset 을 뽑는 순간 전부 어긋난다.)
 *
 * 렌더가 참조하는 것: Util(import), $j.F.Admin.Util.openOrderHandle(canHandle 일 때만).
 */
import Util from '../../Common/Util';


// ───────── 업종별 배경색 (`업종(유형)` 컬럼 셀) — 값은 _injectStyle() 한 곳에 ─────────
const CAR_TYPE_CLASS = {
    '스카이': 'ol-car--sky',
    '사다리': 'ol-car--ladder',
    '크레인': 'ol-car--crane',
    '지게차': 'ol-car--fork',
    '굴삭기': 'ol-car--excav',
    '리프트': 'ol-car--lift',
};

// ───────── 결제방식 라벨 (g5_orders.pay_method 코드 → 한글) ─────────
// 인덱스가 곧 pay_method 코드. 0 은 방어용 더미, 3(직수무)/4 는 현재 미사용.
const HOWTOPAY_LABELS = ['정의된값없음', '싸인', '직수', '직수무', '', '카드'];

// ───────── 오더상태 라벨 (g5_orders.order_status 코드 → 한글) ─────────
// 정본은 PHP `G5_ORDER_STATUS` — 서버가 /asset/bootstrap 으로 G5 에 심는다.
// 예전엔 여기·OrderListModal·MobileAdmin/Order 세 벌이 각자 있었고 서로 달랐다
// (2 를 '알림준비중'/'알림준비' 로, 11 회원삭제는 madmin 만 알았다).
const ORDER_STATUS_LABELS = (typeof G5 !== 'undefined' && G5['ORDER_STATUS']) || {};

const C = 'text-center';

export default class ListTable {

    /** 관리자 접수페이지의 컬럼 순서 (기존 /adm/orders 와 동일) */
    static get DEFAULT_ORDER() {
        return ['orderNum', 'carType', 'balName', 'balCode', 'gisa', 'center', 'address',
                'workTime', 'dayType', 'workHour', 'addHour', 'price', 'feeRate', 'carRange',
                'option', 'howtopay', 'status', 'workerName', 'workerCode', 'fieldNumber',
                'carPlate', 'bonsaCol', 'memo'];
    }

    /**
     * 전체 컬럼 정의 (key → DataTables column def).
     * @param {object} o create() 에 넘긴 옵션 (canHold / canPortal / canHandle / canMatch / managerLoginLevel)
     */
    static columns(o = {}) {
        const Self = this;
        const level = parseInt(o.managerLoginLevel || 0, 10);

        return {
            // 오더번호
            orderNum: {
                data: 'order_num', className: C,
                render: function (data) {
                    const num = String(data).padStart(6, '0');
                    // 관리자만 오더핸들 팝업. 협력사는 링크 없이 번호만.
                    if (!o.canHandle) return num;
                    return `<a href="javascript:$j.F.Order.ListTable.detailOrderOpen(${data})">${num}</a>`;
                },
                // ⚠ '배차완료+call 미확인' 주황 경고는 **수주자명 칸**에만 준다 (workerName.createdCell).
                // 여기에도 걸었더니 오더번호까지 주황이 돼 CS 가 보던 화면과 달라졌다.
            },

            // 업종(유형)
            carType: {
                data: 'car_type', className: C,
                render: function (data, type, row) {
                    let typeStr = row.order_type_str || '';
                    typeStr = typeStr.replace('지정', '<span class="ol-t--assign ol-bold">지정</span>');
                    typeStr = typeStr.replace('어플', '<span class="ol-t--app ol-bold">어플</span>');
                    if (typeStr === '외부') typeStr = '<span class="ol-t--ext ol-bold">외부</span>';
                    const inner = `<span class="ol-t--name">${data}</span><span class="ol-t--type">[${typeStr}]</span>`;
                    if (!o.canMatch) return inner;
                    return `<a href="javascript:$j.F.Order.ListTable.evtNoticeShow(${row.order_num})">${inner}</a>`;
                },
                createdCell: function (td, cellData) {
                    $(td).css('padding', '0px').addClass(CAR_TYPE_CLASS[cellData] || 'ol-car--sky');
                },
            },

            // 발주자명 (+ 사진 아이콘 / 경고회원 배지) — 클릭 시 상세 행 확장
            balName: {
                data: 'bal_name', className: C,
                render: function (data, type, row) {
                    // 사진 장수 — 있을 때만 숫자 뱃지. 칸 왼쪽 끝(경고 띠 자리)에 붙는다
                    const sIcon = row.photo_cnt > 0 ? `<span class="ol-photo-cnt ol-bold">${row.photo_cnt}</span>` : '';
                    // 제재 딱지 — g5_member_owners.penalty (0정상/1경고/2차단/3환원)
                    // 경고(1)·환원(3)은 딱지 대신 칸 앞에 노란·빨간 띠를 칠한다 (createdCell)
                    const pcard = Self.bandClass(row.penalty1) ? '' : $j.F.Admin.Util.penaltyCard(row.penalty1);
                    const picon = pcard ? ` ${pcard}` : '';
                    // ⚠️ 4번째 인자는 검색 인풋에 꽂히는 회원번호다. 반드시 발주자명(bal_name = 소유자명)과
                    // 같은 사람인 order_owner_num 이어야 한다. 예전엔 order_member_num(실제 발주한
                    // 회원 = 소속자일 수 있음)을 넘겨서, 이름은 사장인데 번호는 소속자인 상태로 검색돼
                    // 항상 0건이 나왔다 (소속자 번호는 order_owner_num 에 존재하지 않는다).
                    return `<span class="ol-t--pwrap" onclick="$j.F.Order.ListTable.openTab('${Self.escapeStr(row.bal_owner_name || data)}',1,${row.order_num},${row.order_owner_num})" style="cursor:pointer">${sIcon}${Self.fitName(data)}${picon}</span>`;
                },
                createdCell: function (td, cellData, rowData) {
                    $(td).addClass('ol-cell--person');
                    $(td).addClass(Self.bandClass(rowData.penalty1));
                },
            },

            // 코드(발주) — 누르면 기사편집창을 바로 연다. 관리자 전용.
            // 다른 회원 메뉴(불러톡·서류심사 등)는 발주자명 클릭 → 상세의 회원표기 팝오버에 있다.
            balCode: {
                data: 'order_owner_num', className: C,
                render: function (data) {
                    if (!o.canPortal || !data) return data || '';
                    return `<a href="javascript:void(0)" onclick="$j.F.Admin.Util.memberPortal(${Util.int(data)})">${data}</a>`;
                },
            },

            gisa:   { data: 'sosok_gisa', className: C, render: Self.renderText },
            center: { data: 'sosok_center', className: C, render: Self.renderText },

            // 현장주소 — 클릭 시 지도 팝업
            address: {
                data: 'site_address',
                render: function (data, type, row) {
                    const pts = (row.site_latlng || '').split(',');
                    const p1 = (pts[0] || '').replace(/[^0-9.\-]/g, '');
                    const p2 = (pts[1] || '').replace(/[^0-9.\-]/g, '');
                    return `<a href="javascript:$j.F.Order.ListTable.showMap('${p1}','${p2}')">${Self.escapeHtml(data || '')}</a>`;
                },
                createdCell: function (td, cellData, rowData) {
                    // scs.js orderStatusSet() 이 메모 팝업 제목으로 읽는 셀 (오더 확인체크 기능)
                    if (o.canMemo) $(td).attr('id', 'drow_adr_' + rowData.order_num);
                },
            },

            // 작업일시
            workTime: {
                data: 'work_time', className: C,
                render: function (data) {
                    if (!data || data.length < 16) return data || '';
                    const datePart = data.substring(5, 10).replace(/-/g, '.');
                    const timePart = data.substring(11, 16).replace(/-/g, '.');
                    return `<span class="ol-t--date ol-bold" style="letter-spacing:-1px;font-size:11pt">${datePart}</span> <span class="ol-t--time ol-bold" style="letter-spacing:-1px;font-size:10pt">${timePart}</span>`;
                },
                createdCell: function (td, cellData, rowData) {
                    $(td).css('padding', '0px').addClass('ol-cell--worktime');
                    // scs.js orderStatusSet() 이 메모 팝업 제목으로 읽는 셀 (오더 확인체크 기능)
                    if (o.canMemo) $(td).attr('id', 'drow_wdh_' + rowData.order_num);
                },
            },

            dayType: { data: 'work_day_type', className: C, render: Self.renderText },

            workHour: {
                data: 'work_hour_str', className: C,
                render: function (data) {
                    return (data || '').replace('하루', '<span class="ol-t--assign ol-bold">하루</span>');
                },
            },

            addHour: { data: 'add_hour', className: C },

            price: {
                data: 'car_price', className: C,
                render: function (data) { return Util.formatNumber(data); },
            },

            // 수수료 (+ 머큐리 계산서 발/미 배지, 환원 표시)
            feeRate: {
                data: 'fee_rate', className: C,
                render: function (data, type, row) {
                    let str = data + '%';
                    if (row.invoice_badge === 'done') {
                        str += ' <span class="ol-badge--sent" style="display:inline-block;font-size:8px;padding:0 3px;border-radius:2px;margin-left:2px;vertical-align:middle;line-height:14px">발</span>';
                    } else if (row.invoice_badge === 'pending') {
                        str += ' <span class="ol-badge--unsent" style="display:inline-block;font-size:8px;padding:0 3px;border-radius:2px;margin-left:2px;vertical-align:middle;line-height:14px">미</span>';
                    }
                    if (row.whan_status == 2) str += '<br><span class="ol-t--whan">환원</span>';
                    return str;
                },
            },

            carRange: {
                data: 'car_range_str', className: C, render: Self.renderText,
                createdCell: function (td) { $(td).css('word-break', 'break-word'); },
            },

            option: { data: 'option_str', className: C, render: Self.renderText },

            // 책임(결제)
            howtopay: {
                data: 'pay_method', className: C,
                render: function (data, type, row) {
                    let label = HOWTOPAY_LABELS[data] || '';
                    if (label === '싸인' && row.sign_due) {
                        label += '<span class="ol-t--due">(' + row.sign_due + ')</span>';
                    }
                    return label;
                },
            },

            // 진행상태
            status: {
                data: 'order_status', className: C,
                render: function (data, type, row) {
                    const s = parseInt(data, 10);
                    const t = parseInt(row.order_type, 10);
                    const isRetun = row.is_return_job == 1;

                    if (s === 99) return '<span class="ol-t--cancel">배차취소</span>';
                    // 환원보류 — 진행상태(order_status)와는 무관한 별도 플래그다. 환원배치만 잠깐 멈춘 것이라
                    // 지정대기/회차 같은 진행상태 분기와 얽히지 않고, 수주대기처럼 독립적으로 색만 다르게 표시한다.
                    if (row.whan_status == 1) return '<span class="ol-t--onwhanhold ol-bold">환원보류</span>';
                    // 지정 오더(95) + 알림중(3) + 회차아님 → "지정대기" (레거시 skw_2000n.php 동일)
                    if (s === 3 && t === 95 && !isRetun) return '지정대기';
                    // 회차는 취소(99)·지정대기 외 모든 상태를 덮어쓴다 (레거시 skw_2000n.php:1024)
                    if (isRetun) return '<span class="ol-t--return">회차</span>';
                    if (s === 3) return `<span class="ol-t--pushcnt ol-bold">(${(row.push_progress || '').replace('(', '<br>(')})</span>`;
                    if ((s === 8 || s === 9) && row.is_paid == 1) {
                        if (s === 8) return '<span class="ol-t--done">작업완료</span>';
                        return '수금완료';
                    }
                    if (s === 9) return '수금완료';
                    if (s === 0) return '<span class="ol-t--incomplete">*불완전*</span>';
                    return ORDER_STATUS_LABELS[s] || '';
                },
                createdCell: function (td, cellData, rowData) {
                    // 환원보류 — 수주대기(4)가 마룬 배경으로 튀는 것과 같은 방식. 진행상태 값과 무관하게 독립적으로 칠한다.
                    if (rowData.whan_status == 1) { $(td).addClass('ol-cell--whanhold'); return; }
                    // 수주대기(4) 강조 — 라벨이 "회차"로 덮인 행은 제외 (레거시 skw_2000n.php:1025)
                    if (rowData.is_return_job == 1) return;
                    if (parseInt(cellData, 10) === 4) $(td).addClass('ol-cell--status4');
                },
            },

            // 수주자명 — 관리자(level>=5)는 홀드 input, 그 외는 상세 행 확장 링크
            workerName: {
                data: 'driver_name', className: C,
                render: function (data, type, row) {
                    if (o.canHold && row.is_hold_target == 1 && level >= 5) {
                        const holdVal = row.hold_mb_no || '999999';
                        return `<input type="text" id="reserv_set_${row.order_num}" style="width:60px;font-size:10pt;text-align:center" value="${holdVal}"><input type="button" value="홀드" style="width:50px;height:20px" onclick="$j.p.reservOrder(${row.order_num},${row.order_status})">`;
                    }
                    if (parseInt(row.order_type, 10) === 94) return `<span class="ol-t--pwrap">${Self.fitName(data)}</span>`;
                    // 수주자도 같은 딱지를 쓴다. 예전엔 경고(1)만 그렸는데 발주자칸과 규칙이
                    // 달라 "발주자는 빨간딱지인데 수주자는 왜 없지" 가 됐다
                    const pcard2 = Self.bandClass(row.penalty2) ? '' : $j.F.Admin.Util.penaltyCard(row.penalty2);
                    const picon2 = pcard2 ? ` ${pcard2}` : '';
                    // 검색 인풋에 꽂히는 번호는 수주자 검색이 보는 컬럼(worker_owner_num)과 같아야 한다.
                    // 미배차라 소유자가 없으면 번호 없이 이름만 넣는다 (worker_num 은 회원번호가 아니다).
                    const eamm = row.worker_owner_num > 0 ? row.worker_owner_num : 0;
                    return `<span class="ol-t--pwrap"><a href="javascript:void(0)" onclick="$j.F.Order.ListTable.openTab('${Self.escapeStr(row.soo_owner_name || data || '')}',2,${row.order_num},'${eamm}')">${Self.fitName(data)}</a>${picon2}</span>`;
                },
                createdCell: function (td, cellData, rowData) {
                    // 정렬·폭은 발주자명과 같다. 홀드 입력칸은 86px 에 안 들어가므로 뺀다
                    if (!(o.canHold && rowData.is_hold_target == 1 && level >= 5)) $(td).addClass('ol-cell--person ol-cell--soo');
                    if (parseInt(rowData.order_type, 10) !== 94) $(td).addClass(Self.bandClass(rowData.penalty2));
                    // 배차완료(6) 인데 수주자 call 미확인 → 주황색 경고 (레거시 skw_2000n.php)
                    if (parseInt(rowData.order_status, 10) === 6 && Util.int(rowData.bal_call_confirmed) !== 1) {
                        $(td).addClass('ol-cell--hold');
                    }
                },
            },

            // 코드(수주) — 누르면 기사편집창을 바로 연다. 관리자 전용. (balCode 와 같다)
            workerCode: {
                data: 'worker_code', className: C,
                render: function (data, type, row) {
                    if (!data) return '';
                    if (!o.canPortal) return data;
                    // 서버가 내리는 worker_code = worker_owner_num(소유자번호). 편집창도 같은 번호로 연다.
                    return `<a href="javascript:void(0)" onclick="$j.F.Admin.Util.memberPortal(${Util.int(row.worker_owner_num)})">${data}</a>`;
                },
            },

            // 현장번호 — 관리자(level>=7)는 홀드 대상자 표시
            fieldNumber: {
                data: 'site_phone', className: C,
                render: function (data, type, row) {
                    if (o.canHold && row.is_hold_target == 1 && level >= 7 && row.hold_mb_no) {
                        return `<span class="ol-t--held">${row.hold_mb_no} 에게 홀드됨</span>`;
                    }
                    return Self.escapeHtml(data || '');
                },
            },

            carPlate: { data: 'car_plate', className: C, render: Self.renderText },

            // 접수번호 (본사별 커스텀 컬럼. 서버가 meta.receipt_no / meta.site_name 에서 뽑아준다)
            bonsaCol: { data: 'bonsa_col', className: C, render: Self.renderText },

            // 작업메모 (+ 오더 확인체크 아이콘 — canMemo 일 때만)
            memo: {
                data: 'request_note',
                render: function (data, type, row) {
                    let msg = Self.escapeHtml(data || '');
                    if (row.admin_memo) msg += '&nbsp;<span class="ol-t--memo"> / ' + Self.escapeHtml(row.admin_memo) + '</span>';

                    // 확인체크 아이콘. 클릭 → scs.js orderStatusSet() → 메모/상태 팝업.
                    // img id(data_row_img_N)·행 id(data_row_N)를 scs.js 가 직접 갱신하므로 이름을 맞춰야 한다.
                    if (o.canMemo) {
                        const done = row.is_scs_review == 1;
                        const img = done ? 'icon_gisaok' : 'icon_gisayet';
                        const icon = `<img src="/img/${img}.png" id="data_row_img_${row.order_num}" style="height:20px;position:relative;top:5px;padding-right:3px;cursor:pointer" onclick="orderStatusSet(this,${row.order_num},${done ? 1 : 0})">`;
                        return icon + msg;
                    }
                    return msg;
                },
                createdCell: function (td) { $(td).css({ 'word-break': 'break-all', 'min-width': '150px' }); },
            },

            // ─── 보험 정산 소속(머큐리 022) 전용 — 레거시 scs/main3.php ───
            insCompany: { data: 'ins_company', className: C, defaultContent: '', render: Self.renderText },
            insTeam:    { data: 'ins_team',    className: C, defaultContent: '', render: Self.renderText },
            insManager: { data: 'ins_manager', className: C, defaultContent: '', render: Self.renderText },
            insPhone:   { data: 'ins_phone',   className: C, defaultContent: '', render: Self.renderText },
        };
    }

    /**
     * 오더목록 DataTable 생성.
     * @returns DataTable 인스턴스
     */
    /**
     * 표 색을 **한 곳에서** 주입한다. 1회만 (id 가드).
     *
     * 예전엔 이 파일 26곳에 흩어져 있었다 — `$(td).css()` 6곳 + 템플릿 문자열 인라인 20곳.
     * 그래서 **어떤 CSS 토큰화도 접수목록 표에 닿지 못했다**: sass 를 아무리 정리해도 색은
     * 런타임에 인라인으로 다시 칠해졌고, /adm/theme 점검도 (radius/shadow 만 세므로) 못 잡았다.
     * (F/Admin/Member/ListTable.js 가 같은 방식을 이미 쓰고 있다.)
     *
     * ⚠ **값은 옮기기만 했다 — 하나도 바꾸지 않았다.** 라이트 픽셀 변화 0.
     *
     * ⚠ 다크는 아직 없다. 아래 `.ol-t--*` 중 여덟(환원·마감·배차취소·회차·작업완료·불완전·
     *   홀드됨·오류)이 **전부 `red` 하나를 쓴다.** 뜻이 겹친 채로는 다크 색을 배정할 수 없다 —
     *   먼저 "이 빨강들이 같은 뜻인가"를 CS 와 갈라야 한다. 그래서 여기서는 각각 **이름만**
     *   주고 값은 묶어 두었다. 이 목록이 곧 물어볼 항목이다.
     *
     * 이 파일은 /adm/orders 와 /p/orders(협력사)가 공유한다. 협력사는 ThemeBoot 가 없어
     * 다크가 아예 안 걸리므로, 나중에 다크값을 넣어도 그쪽은 그대로다.
     */
    static _injectStyle() {
        if (document.getElementById('order-listtable-style')) return;
        const css = `
/* 셀 배경 — 상태 */
.ol-cell--warn{box-shadow:inset var(--ol-band,16px) 0 0 #FFE680}  /* 경고회원(penalty 1) — 딱지 대신 칸 앞에 노란 띠 */
.ol-cell--whan{box-shadow:inset var(--ol-band,16px) 0 0 #FF6B6B}  /* 환원페널티(penalty 3) — 빨간 띠 */
.ol-cell--hold{background-color:#FF8000}
.ol-cell--worktime{background-color:#D9D9B3}
.ol-cell--whanhold{background-color:#7a5c00;color:#FFFFFF}
.ol-cell--status4{background-color:#800000;color:#FFFFFF}
/* 셀 배경 — 업종 6색 (CAR_TYPE_CLASS) */
.ol-car--sky{background-color:#FFFFFF}
.ol-car--ladder{background-color:#00FFFF}
.ol-car--crane{background-color:#FFCEB7}
.ol-car--fork{background-color:#FFFF80}
.ol-car--excav{background-color:#D2D2FF}
.ol-car--lift{background-color:#EAD5FF}
/* 굵게 — **반드시 이 클래스로 준다.**
 * sass pages/_order-list-legacy.scss 가 #order-table 안 모든 자식의 굵기를 400 으로 강제하고(Medium 합성 차단),
 * 스스로 굵기를 선언한 것만 예외로 둔다. 그 예외가 .ol-bold 다 — 색 클래스에
 * font-weight 를 적으면 명시도(1,1,1)에 밀려 조용히 400 으로 눌린다. */
.ol-bold{font-weight:bold}
/* 발주유형·이름 */
.ol-t--assign{color:#0080FF}    /* 지정 · 하루 */
.ol-t--app{color:red}           /* 어플 */
.ol-t--ext{color:blue}          /* 외부 */
.ol-t--name{color:blue}
.ol-t--type{color:#800000}
.ol-t--pwrap{display:inline-flex;align-items:center;justify-content:center;vertical-align:middle}
/* 작업일시 */
.ol-t--date{color:red}
.ol-t--time{color:#004080}
/* 사인 발송여부 뱃지 */
.ol-badge--sent{background:#1a7f37;color:#fff}
.ol-badge--unsent{background:#cf222e;color:#fff}
/* 사진 장수 뱃지 */
/* 발주자명·수주자명 칸 — 86px 고정. 왼쪽 16px 은 경고 띠 자리,
 * 발주자명은 그 왼쪽 위에 사진 장수가 붙는다.
 * 이름은 칸 한가운데. 좌우 여백을 띠 폭(16px)으로 똑같이 둬서 띠에 안 닿으면서 치우치지도 않는다
 * (왼쪽만 비우면 이름이 오른쪽으로 쏠린다). 띠 유무와 상관없이 모든 줄이 같은 위치.
 * 4글자 이하는 한 줄. 길면 4글자 폭마다 <br> 로 꺾어 줄 수 제한 없이 다 보인다 (fitName).
 * 줄을 CSS 가 아니라 fitName 이 나누는 이유 — CSS 로 꺾으면 상자가 꺾을 폭(4.4em) 그대로라 실제 글자보다 넓어
 *   가운데 정렬해도 글자 덩어리가 한쪽으로 쏠린다. 직접 나누면 상자 폭 = 가장 긴 줄이라 정확히 가운데다
 * 발주자명 열 폭은 _order-list-legacy.scss 에도 같은 값으로 걸려 있다 (th 까지 잡아야 해서).
 * ⚠ 접수목록 sass 의 body[data-page-name="OrderList"] td{padding-left:5px !important} (0,1,2) 를 이겨야 해서 html body td 로 쓴다 */
.ol-cell--person{position:relative}
html body td.ol-cell--warn,
html body td.ol-cell--whan{padding-left:20px !important}
html body td.ol-cell--person{padding-left:var(--ol-band,16px) !important;padding-right:var(--ol-band,16px) !important;min-width:var(--ol-name-w,86px);max-width:var(--ol-name-w,86px);white-space:nowrap}
/* 수주자명은 홀드 입력칸(999999+[홀드], 70px) 폭에 맞춰 72px — 띠·여백 12 + 이름 4글자(48) + 12 */
td.ol-cell--soo{--ol-band:12px;--ol-name-w:72px}
.ol-cell--person .ol-t--pwrap{display:flex;width:100%}
.ol-cell--person .ol-t--pwrap{justify-content:center}
.ol-cell--person .ol-photo-cnt{position:absolute;left:1px;top:2px}
.ol-cell--person .ol-t--pwrap > a{display:flex;justify-content:center;flex:0 1 auto;min-width:0}
.ol-t--bname{flex:none;white-space:nowrap;line-height:1.2;text-align:center}  /* 상자는 가장 긴 줄 폭, 줄마다 가운데 */
.ol-t--bname-s{flex:0 1 auto;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.ol-photo-cnt{flex:none;min-width:15px;height:15px;padding:0 3px;box-sizing:border-box;border-radius:8px;font-size:11px;line-height:15px;text-align:center;background-color:#0080FF;color:#FFFFFF}
/* 진행상태 — 파랑 */
.ol-t--pushcnt{color:blue}                      /* 발송횟수 */
.ol-t--memo{color:blue}                         /* 관리자메모 */
.ol-t--onwhanhold{color:#fff}                   /* 환원보류 (어두운 셀 위) */
/* 진행상태 — 빨강 8종. ⚠ 지금은 값이 같지만 **뜻이 다르다.** 다크 배정 전에 갈라야 한다. */
.ol-t--whan,        /* 환원 */
.ol-t--due,         /* 사인 마감 */
.ol-t--cancel,      /* 배차취소 */
.ol-t--return,      /* 회차 */
.ol-t--done,        /* 작업완료 */
.ol-t--incomplete,  /* *불완전* */
.ol-t--held,        /* 남에게 홀드됨 */
.ol-t--error        /* 상세 로드 실패 */
{color:red}

/* ── 다크 ───────────────────────────────────────────────────────────────────
 * 원칙: **색상(hue)은 그대로, 명도만 뒤집는다.** CS 가 "크레인은 주황"·"환원은 빨강"으로
 * 계속 읽어야 하므로 색을 재배정하지 않았다. 채움은 어둡게, 글자는 밝은 쪽으로 옮긴다.
 * 빨강 8종은 여전히 한 값이다 — 뜻을 가르는 것은 별개의 결정이라 여기서 하지 않았다. */
html[data-theme="dark"] .ol-cell--warn{box-shadow:inset var(--ol-band,16px) 0 0 #8a7a2a}
html[data-theme="dark"] .ol-cell--whan{box-shadow:inset var(--ol-band,16px) 0 0 #9a3434}
html[data-theme="dark"] .ol-cell--hold{background-color:#8a4600}
html[data-theme="dark"] .ol-cell--worktime{background-color:#454429}
html[data-theme="dark"] .ol-car--sky{background-color:#3a4046}
html[data-theme="dark"] .ol-car--ladder{background-color:#0e4a4a}
html[data-theme="dark"] .ol-car--crane{background-color:#5a3a2c}
html[data-theme="dark"] .ol-car--fork{background-color:#4d4a1e}
html[data-theme="dark"] .ol-car--excav{background-color:#33375c}
html[data-theme="dark"] .ol-car--lift{background-color:#41335c}
html[data-theme="dark"] .ol-t--assign{color:#66b3ff}
html[data-theme="dark"] .ol-t--app{color:#ff8a80}
html[data-theme="dark"] .ol-t--ext{color:#93c5fd}
html[data-theme="dark"] .ol-t--name{color:#93c5fd}
html[data-theme="dark"] .ol-t--type{color:#f28b82}
html[data-theme="dark"] .ol-t--date{color:#ff8a80}
html[data-theme="dark"] .ol-t--time{color:#8ab4f8}
html[data-theme="dark"] .ol-t--pushcnt{color:#93c5fd}
html[data-theme="dark"] .ol-t--memo{color:#93c5fd}
html[data-theme="dark"] .ol-t--whan,
html[data-theme="dark"] .ol-t--due,
html[data-theme="dark"] .ol-t--cancel,
html[data-theme="dark"] .ol-t--return,
html[data-theme="dark"] .ol-t--done,
html[data-theme="dark"] .ol-t--incomplete,
html[data-theme="dark"] .ol-t--held,
html[data-theme="dark"] .ol-t--error
{color:#ff8a80}
`;
        $('<style id="order-listtable-style">').text(css).appendTo('head');
    }

    static create(o = {}) {
        this._injectStyle();
        // 렌더 HTML 안의 onclick 이 참조하므로 옵션을 모듈에 보관 (페이지당 오더테이블 1개 전제)
        this._o = o;

        const defs = this.columns(o);
        const keys = (o.columns && o.columns.length) ? o.columns : this.DEFAULT_ORDER;
        const columns = keys.map((k) => defs[k]).filter(Boolean);
        this._colCount = columns.length;

        const getSearch = (typeof o.getSearch === 'function') ? o.getSearch : () => ({});

        if ($.fn.DataTable.isDataTable(o.selector)) {
            try { $(o.selector).DataTable().destroy(); } catch (e) { /* noop */ }
        }

        const table = $(o.selector).DataTable({
            processing: true,
            serverSide: true,
            searching: false,  // 내장 검색창 비활성화 (자체 필터 UI 사용)
            ordering: false,   // 내장 정렬 비활성화 (thead 클릭 핸들러로 대체)
            autoWidth: false,  // inline 너비를 박으면 display:none 시 우측 여백이 생긴다 → CSS 에 위임
            pageLength: (o.pageLength != null) ? o.pageLength : 50,
            lengthChange: false,
            ajax: {
                url: o.url,
                type: 'post',
                data: function (d) { Object.assign(d, getSearch()); },
                dataSrc: function (json) {
                    if (typeof o.onLoaded === 'function') o.onLoaded(json);
                    return json.data;
                },
            },
            columns,
            rowCallback: function (row, data) {
                // is_special 등 플래그는 "0"/"1" 문자열로 내려오므로 truthy 체크 금지
                if (data.is_special == 1) $(row).addClass('star');
                // 행 확장(openTab)이 오더번호 컬럼 위치에 의존하지 않도록 행에 오더번호를 박아둔다
                $(row).attr('data-order-num', data.order_num);

                // 오더 확인체크: scs.js setOrderStatusSetTo() 가 이 id 로 행을 찾아 배경색을 바꾼다
                if (o.canMemo) {
                    $(row).attr('id', 'data_row_' + data.order_num);
                    if (data.is_scs_review == 1) $(row).addClass('adonetr');
                }
            },
            language: {
                processing: '로딩중...',
                emptyTable: '데이터가 없습니다.',
                info: '_START_ ~ _END_ / _TOTAL_건',
                paginate: { first: '처음', last: '마지막', next: '다음', previous: '이전' },
            },
        });

        this._table = table;

        // 헤더 클릭 정렬: 같은 컬럼 재클릭 시 asc↔desc 토글, 다른 컬럼이면 desc 로 리셋.
        // data-col 이 없는 th(계산 컬럼)는 무시.
        if (typeof o.onSort === 'function') {
            $(o.selector + ' thead th').on('click', function () {
                const col = $(this).data('col');
                if (!col) return;
                o.onSort(col);
            });
        }

        return table;
    }

    /** 현재 테이블 인스턴스 */
    static table() { return this._table; }

    // ==================== 행 확장 (오더 상세) ====================
    /**
     * 오더 행 아래에 상세 정보 tr 을 동적으로 삽입한다.
     * 같은 오더를 다시 클릭하면 토글로 닫히고, 다른 오더 클릭 시 기존 상세는 닫는다.
     *
     * @param {string} name     발주자/수주자 이름 (검색 인풋에 반영)
     * @param {number} smode    1=발주자 클릭, 2=수주자 클릭
     * @param {number} ordernum 오더번호
     * @param {number|string} mb_no 소유자 회원번호 — 발주=order_owner_num, 수주=worker_owner_num.
     *                              검색이 실제로 보는 컬럼과 같은 축이어야 한다 (0 이면 이름만 넣는다)
     */
    static openTab(name, smode, ordernum, mb_no) {
        const o = this._o || {};
        const num = parseInt(mb_no, 10) || 0;
        if (name === '' && num > 0) name = num;
        // 검색 인풋에 '이재일#2831' 로 꽂아둔다. 검색 실행은 하지 않는다 (사용자가 검색 버튼으로).
        if (smode === 1) $j.F.Order.SearchOpt.setMemberInput('wb', name, num);
        if (smode === 2) $j.F.Order.SearchOpt.setMemberInput('ws', name, num);

        // 토글: 이미 열려있으면 닫기
        const $existing = $('#eaTr_' + ordernum);
        if ($existing.length) { $existing.remove(); return; }

        // 다른 상세행은 닫기 (한 번에 하나만)
        $('.hidden_order_tab').remove();

        // rowCallback 이 박아둔 data-order-num 으로 행을 찾는다 (구: td:first 텍스트 비교 → 컬럼 순서에 취약)
        const $row = $(o.selector + ' tbody tr[data-order-num="' + ordernum + '"]');
        if (!$row.length) return;

        const colspan = this._colCount || 23;
        const $detailRow = $(`<tr id="eaTr_${ordernum}" class="hidden_order_tab"><td colspan="${colspan}" style="text-align:center;height:50px">정보 불러오는 중...</td></tr>`);
        $row.after($detailRow);

        this._currentOrdernum = ordernum;

        $.get(`${o.detailUrl}?ordernum=${ordernum}`, function (html) {
            $detailRow.find('td').html(html);
            ListTable.renderMatchTags($detailRow);
        }).fail(function () {
            $detailRow.find('td').html('<span class="ol-t--error">정보를 불러올 수 없습니다.</span>');
        });
    }

    /**
     * 상세 HTML 안의 `.js-mtag` 자리에 매칭 유형 뱃지를 그린다.
     *
     * 뱃지 표기(NC ver4 · 색)의 정본은 $j.F.Admin.MatchTag 하나뿐이다 — 서버가 같은 마크업을
     * 문자열로 찍으면 배분분포·매칭리스트와 조용히 갈라진다. 그래서 블레이드는 dt/ver 만
     * data 속성으로 심어두고, 그리는 일은 여기서 그 모듈에 맡긴다.
     * (협력사 화면에는 모듈이 없을 수 있어 없으면 조용히 지나간다 — 자리 span 은 비어 보인다)
     */
    static renderMatchTags($root) {
        const MT = $j.F.Admin && $j.F.Admin.MatchTag;
        if (!MT) return;
        $root.find('.js-mtag').each(function () {
            $(this).html(MT.badge(this.dataset.dt || '', this.dataset.ver || 0));
        });
    }

    /** 현재 열려있는 상세탭 오더번호 (외부 창에서 orderDetail_show 로 교체할 때 참조) */
    static currentOrdernum() { return this._currentOrdernum; }

    /** 외부 창/iframe 에서 상세탭 HTML 을 교체 (레거시 parent.orderDetail_show) */
    static orderDetailShow(val) {
        if (!this._currentOrdernum) return;
        const colspan = this._colCount || 23;
        $('#eaTr_' + this._currentOrdernum).html(`<td colspan="${colspan}" style="text-align:center;height:50px">${val}</td>`);
    }

    // ==================== 팝업 ====================
    /** 오더번호 클릭 → 오더 핸들링 팝업 (관리자 전용, $j.F.Admin.Util) */
    static detailOrderOpen(num) { $j.F.Admin.Util.openOrderHandle(num); }

    /** 업종 클릭 → 매칭내역(SMS 발송 통합 UI) 팝업 (관리자 전용) */
    static evtNoticeShow(num) {
        const adr = `/adm/order/match/list?num=${num}&stype=admin`;
        // 창 높이를 화면 가용높이로 클램프 — 창이 화면보다 크면 아래가 잘리고 스크롤바가 안 생김
        const w = Math.min(1400, screen.availWidth), h = Math.min(1000, screen.availHeight);
        const win = window.open(adr, 'manage_order_matchList', `toolbar=no,status=no,width=${w},height=${h},top=0,directories=no,scrollbars=yes,location=no,resizable=yes,menubar=no`);
        if (win) win.focus();
    }

    /** 현장주소 클릭 → 지도 팝업 */
    static showMap(pos1, pos2) {
        const adr = `/map/full?gps=${pos1},${pos2}`;
        const win = window.open(adr, 'mapshowwin', 'toolbar=no,status=no,WIDTH=800,HEIGHT=500,directories=no,scrollbars=yes,location=no,resizable=yes,menubar=no');
        if (win) win.focus();
    }

    /**
     * 행 확장 상세(adm/pages/order/detail.blade.php)의 클릭 — 상세는 ajax 로 주입되므로 문서에 위임한다.
     * 접수목록(/adm/orders)·협력사 접수목록(/p/orders)·삼성 접수목록이 각자 한 번 부른다.
     * @param showPic (ordernum, n) => void — 사진 보기는 화면마다 창 옵션이 달라 호출부가 준다
     */
    static bindDetail(showPic) {
        $(document).off('.odetail')
            // 썸네일 → 사진뷰어 창 (data-pic="오더번호,사진번호" — 0 은 전자작업확인서)
            .on('click.odetail', '.od-root [data-pic]', function () {
                const [ono, n] = String(this.dataset.pic).split(',').map(Number);
                showPic(ono, n);
            })
            // 전화번호 → 그 회원으로 대리로그인해 오더 화면 (관리자에게만 링크가 나온다)
            .on('click.odetail', '.od-root [data-user-order]', function (e) {
                e.preventDefault();
                const [mb, ono] = String(this.dataset.userOrder).split(',').map(Number);
                $j.F.Admin.Util.userOrder(mb, ono);
            });
    }

    /**
     * 작업사진 팝업 (행 확장 detail.blade.php 의 썸네일 클릭)
     * @param odr 전자작업확인서 차수. 안 주면 마지막 차수 (관리자 오더접수는 1 — 1차만 본다)
     */
    static showPic(orderno, n, odr = 0) {
        const h = window.screen.availHeight;
        const adr = `/order/photos?ordernum=${orderno}&n=${n}` + (odr ? `&odr=${odr}` : '');
        const win = window.open(adr, 'photoPopupDetail', `toolbar=no,status=no,WIDTH=900,HEIGHT=${h},directories=no,scrollbars=yes,location=no,resizable=yes,menubar=no`);
        if (win) win.focus();
    }

    // ==================== 유틸 ====================
    /**
     * 템플릿 문자열 삽입용 이스케이프.
     * `onclick="openTab('${name}',...)"` 조립에서 따옴표 깨짐 방지용.
     * XSS 방어로는 부족하므로 신뢰할 수 없는 텍스트엔 escapeHtml 을 쓸 것.
     */
    static escapeStr(s) {
        // JS 문자열(\ ' 줄바꿈) → HTML 속성(& " < >) 순으로 막는다. 속성값은 HTML 디코딩 뒤 JS 로 읽힌다
        return String(s || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/[\r\n]/g, ' ')
            .replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /**
     * 제재 띠 — 경고(1)는 노란 띠, 환원페널티(3)는 빨간 띠를 이름칸 앞에 칠하고 딱지는 그리지 않는다.
     * 그외 제재(2, 분홍)는 딱지 그대로. 띠가 없으면 ''.
     */
    static bandClass(penalty) {
        return { 1: 'ol-cell--warn', 3: 'ol-cell--whan' }[Util.int(penalty)] || '';
    }

    /**
     * 발주자·수주자명 — 4글자 이하는 한 줄, 길면 4글자 폭마다 줄바꿈해 전부 보인다 (CSS .ol-t--bname).
     * 폭은 전각 1 · 반각(괄호·숫자·영문) 0.5 로 센다.
     */
    static fitName(s) {
        s = String(s || '');
        // 짧은 이름은 제 길이로 둔다 — 가운데 정렬이 실제 글자 폭 기준이 되게
        if (Array.from(s).length <= 4) return `<span class="ol-t--bname-s">${ListTable.escapeHtml(s)}</span>`;
        const lines = [''];
        let w = 0;
        for (const ch of s) {
            const cw = ch.charCodeAt(0) > 0xFF ? 1 : 0.5;
            if (w + cw > 4) { lines.push(''); w = 0; }
            lines[lines.length - 1] += ch;
            w += cw;
        }
        return `<span class="ol-t--bname">${lines.map(l => ListTable.escapeHtml(l)).join('<br>')}</span>`;
    }

    /** DataTables 텍스트 칸 render — 회원·협력사가 입력한 값을 그대로 HTML 로 넣지 않는다 */
    static renderText(data) {
        return ListTable.escapeHtml(data == null ? '' : String(data));
    }

    /** HTML 엔티티 이스케이프 (사용자 입력을 innerHTML 로 넣을 때) */
    static escapeHtml(s) {
        const d = document.createElement('div');
        d.appendChild(document.createTextNode(s || ''));
        return d.innerHTML;
    }

}
